import { Box } from '@mantine/core';
import Toast from 'components/Toast';
import RevisionFieldCertificate from 'components/forms/revision/fill-out/fields/RevisionFieldCertificate';
import RevisionFieldCheckbox from 'components/forms/revision/fill-out/fields/RevisionFieldCheckbox';
import RevisionFieldConstant from 'components/forms/revision/fill-out/fields/RevisionFieldConstant';
import RevisionFieldComparator from 'components/forms/revision/fill-out/fields/RevisionFieldComparator';
import RevisionFieldDate from 'components/forms/revision/fill-out/fields/RevisionFieldDate';
import RevisionFieldDatePlusPeriod from 'components/forms/revision/fill-out/fields/RevisionFieldDatePlusPeriod';
import RevisionFieldDeviceEvidenceField from 'components/forms/revision/fill-out/fields/RevisionFieldDeviceEvidenceField';
import RevisionFieldDeviceProperty from 'components/forms/revision/fill-out/fields/RevisionFieldDeviceProperty';
import RevisionFieldFileList from 'components/forms/revision/fill-out/fields/RevisionFieldFileList';
import RevisionFieldFieldUpdatedAt from 'components/forms/revision/fill-out/fields/RevisionFieldFieldUpdatedAt';
import RevisionFieldIfElse from 'components/forms/revision/fill-out/fields/RevisionFieldIfElse';
import RevisionFieldMeasuringDevice from 'components/forms/revision/fill-out/fields/RevisionFieldMeasuringDevice';
import RevisionFieldDeviceFieldAggregator from 'components/forms/revision/fill-out/fields/RevisionFieldDeviceFieldAggregator';
import RevisionFieldMeasurementColumnAggregator from 'components/forms/revision/fill-out/fields/RevisionFieldMeasurementColumnAggregator';
import RevisionFieldTextExpression from 'components/forms/revision/fill-out/fields/RevisionFieldTextExpression';
import RevisionFieldNumber from 'components/forms/revision/fill-out/fields/RevisionFieldNumber';
import RevisionFieldNumberOfMeasurements from 'components/forms/revision/fill-out/fields/RevisionFieldNumberOfMeasurements';
import RevisionFieldPeriod from 'components/forms/revision/fill-out/fields/RevisionFieldPeriod';
import RevisionFieldPredefinedList from 'components/forms/revision/fill-out/fields/RevisionFieldPredefinedList';
import RevisionFieldPredefinedSelect from 'components/forms/revision/fill-out/fields/RevisionFieldPredefinedSelect';
import RevisionFieldQualification from 'components/forms/revision/fill-out/fields/RevisionFieldQualification';
import RevisionFieldSafetyPoint from 'components/forms/revision/fill-out/fields/RevisionFieldSafetyPoint';
import RevisionFieldSelect from 'components/forms/revision/fill-out/fields/RevisionFieldSelect';
import RevisionFieldSum from 'components/forms/revision/fill-out/fields/RevisionFieldSum';
import RevisionFieldText from 'components/forms/revision/fill-out/fields/RevisionFieldText';
import RevisionFieldWysiwyg from 'components/forms/revision/fill-out/fields/RevisionFieldWysiwyg';
import RevisionFieldValueMap from 'components/forms/revision/fill-out/fields/RevisionFieldValueMap';
import { nanoid } from 'nanoid';
import { IFormInput } from 'pages/revisions-module/template-editor/editors/form/types';
import { useMemo } from 'react';

type TypeCond<T, U> = [T] extends [never] ? { message: U } : {};

interface RevisionFieldProps {
  field: IFormInput;
  namePrefix?: string;
  deviceId?: number;
}

/**
 * Selects the correct field component based on the field type.
 */
function RevisionFieldDiscriminator({ field: { spec }, deviceId, namePrefix = '' }: RevisionFieldProps) {
  const name = useMemo(() => `${namePrefix}${spec.name}`, [namePrefix, spec.name]);

  switch (spec.type) {
    case 'text':
      return <RevisionFieldText spec={spec} name={name} />;

    case 'number':
      return <RevisionFieldNumber spec={spec} name={name} />;

    case 'date':
      return <RevisionFieldDate spec={spec} name={name} />;

    case 'checkbox':
      return <RevisionFieldCheckbox spec={spec} name={name} />;

    case 'select':
      return <RevisionFieldSelect spec={spec} name={name} />;

    case 'certificate':
      return <RevisionFieldCertificate spec={spec} name={name} />;

    case 'qualification':
      return <RevisionFieldQualification spec={spec} name={name} />;

    case 'wysiwyg':
      return <RevisionFieldWysiwyg spec={spec} name={name} />;

    case 'constant':
      return <RevisionFieldConstant spec={spec} name={name} />;

    case 'valueMap':
      return <RevisionFieldValueMap spec={spec} name={name} deviceId={deviceId} />;

    case 'ifElse':
      return <RevisionFieldIfElse spec={spec} name={name} deviceId={deviceId} />;

    case 'comparator':
      return <RevisionFieldComparator spec={spec} name={name} deviceId={deviceId} />;

    case 'predefinedSelect':
      return <RevisionFieldPredefinedSelect spec={spec} name={name} />;

    case 'predefinedList':
      return <RevisionFieldPredefinedList spec={spec} name={name} />;

    case 'measuringDevice':
      return <RevisionFieldMeasuringDevice spec={spec} name={name} />;

    case 'period':
      return <RevisionFieldPeriod spec={spec} name={name} />;

    case 'safetyPoint':
      return <RevisionFieldSafetyPoint spec={spec} name={name} />;

    case 'deviceProperty':
      return <RevisionFieldDeviceProperty spec={spec} name={name} />;

    case 'deviceEvidenceField':
      return <RevisionFieldDeviceEvidenceField spec={spec} name={name} deviceId={deviceId} />;

    case 'sum':
      return <RevisionFieldSum spec={spec} name={name} deviceId={deviceId} />;

    case 'datePlusPeriod':
      return <RevisionFieldDatePlusPeriod spec={spec} name={name} />;

    case 'faultType':
      // This field type is not used in the form fill-out, so we return an empty fragment.
      return <></>;

    case 'faultSeverity':
      // This field type is not used in the form fill-out, so we return an empty fragment.
      return <></>;

    case 'numberOfMeasurements':
      return <RevisionFieldNumberOfMeasurements spec={spec} name={name} deviceId={deviceId} />;

    case 'measurementColumnAggregator':
      return <RevisionFieldMeasurementColumnAggregator spec={spec} name={name} deviceId={deviceId} />;

    case 'deviceFieldAggregator':
      return <RevisionFieldDeviceFieldAggregator spec={spec} name={name} />;

    case 'textExpression':
      return <RevisionFieldTextExpression spec={spec} name={name} deviceId={deviceId} />;

    case 'fieldUpdatedAt':
      return <RevisionFieldFieldUpdatedAt spec={spec} name={name} deviceId={deviceId} />;

    case 'fileList':
      return <RevisionFieldFileList spec={spec} name={name} />;

    default:
      // This is a TS trick to make sure all types are handled in the switch.
      // If there is a new type, the compiler will complain here. To fix it, add a new case to the switch.
      const { message }: TypeCond<typeof spec, string> = { message: 'Neznámy typ poľa' };

      return (
        <Box mr={32} key={nanoid()}>
          <Toast fullWidth type="fail" withCloseButton={false} message={message} />
        </Box>
      );
  }
}

/**
 * Field in the row of the revision form.
 */
export default function RevisionField({ field, namePrefix, deviceId }: RevisionFieldProps) {
  return (
    <Box display={field.spec.hidden ? 'none' : 'block'}>
      <RevisionFieldDiscriminator field={field} namePrefix={namePrefix} deviceId={deviceId} />
    </Box>
  );
}
